import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiPath, ApiService, AuthService } from '@homeleite/arch';
import { retryExceptOnClientError } from '@homeleite/common-utils';
import { merge, Observable, Subject, EMPTY, of } from 'rxjs';
import {
    catchError,
    debounceTime,
    filter,
    map,
    mergeMap,
    retryWhen,
    shareReplay,
    tap
} from 'rxjs/operators';
import { CurrentInfo } from './current-info';

const CURRENT_CACHE_KEY_PREFIX = 'homeleite.cache.current';
const ENV_STORAGE_KEY = 'homeleiteAdmin.env';

@Injectable({
    providedIn: 'root'
})
export class CurrentInfoService {
    public readonly $value: Observable<CurrentInfo>;

    private readonly $cacheValue = new Subject<CurrentInfo>();

    private target: ApiPath;

    constructor(authService: AuthService, api: ApiService) {
        this.target = api.root.path('current');

        authService.$hasToken.subscribe((hasToken) => {
            if (!hasToken) this.cleanCache();
        });

        const cached = this.loadFromCache();

        this.$value = merge(
            of(cached).pipe(filter((it) => !!it)),
            authService.$hasToken.pipe(
                debounceTime(100),
                filter((hasToken) => hasToken),
                mergeMap(() => {
                    if (!navigator.onLine) {
                        return cached ? of(cached) : EMPTY;
                    }

                    return this.fetchCurrent().pipe(
                        tap((v) => this.saveToCache(v)),
                        catchError((err) => {
                            // 401/403: nunca reutilizar admin de outro ambiente / sessão morta
                            if (this.isAuthFailure(err)) {
                                this.cleanCache();
                                return EMPTY;
                            }
                            const fallback = this.loadFromCache();
                            return fallback ? of(fallback) : EMPTY;
                        })
                    );
                })
            ),
            this.$cacheValue
        ).pipe(shareReplay(1));

        if (cached) this.$cacheValue.next(cached);
    }

    private fetchCurrent(): Observable<CurrentInfo> {
        return this.target
            .path('/full')
            .getJson()
            .pipe(
                map((j) => CurrentInfo.fromJson(j)),
                retryWhen(retryExceptOnClientError())
            );
    }

    private cacheKey(): string {
        try {
            const env = localStorage.getItem(ENV_STORAGE_KEY);
            if (env === 'homologation' || env === 'production') {
                return `${CURRENT_CACHE_KEY_PREFIX}.${env}`;
            }
            if (
                typeof location !== 'undefined' &&
                location.hostname === 'admin-homolog.racaholandesa.com.br'
            ) {
                return `${CURRENT_CACHE_KEY_PREFIX}.homologation`;
            }
        } catch {
            // ignore
        }
        return `${CURRENT_CACHE_KEY_PREFIX}.production`;
    }

    private loadFromCache(): CurrentInfo {
        const keys = [this.cacheKey(), CURRENT_CACHE_KEY_PREFIX];
        for (const key of keys) {
            const str = localStorage.getItem(key);
            if (str) {
                try {
                    return CurrentInfo.fromJson(JSON.parse(atob(str)));
                } catch {
                    localStorage.removeItem(key);
                }
            }
        }
        return null;
    }

    private cleanCache() {
        try {
            localStorage.removeItem(CURRENT_CACHE_KEY_PREFIX);
            localStorage.removeItem(`${CURRENT_CACHE_KEY_PREFIX}.production`);
            localStorage.removeItem(`${CURRENT_CACHE_KEY_PREFIX}.homologation`);
        } catch {
            // ignore
        }
        this.$cacheValue.next(null);
    }

    clear() {
        this.cleanCache();
    }

    private saveToCache(value: CurrentInfo) {
        localStorage.setItem(
            this.cacheKey(),
            btoa(JSON.stringify(value.toJson()))
        );
        // remove legado compartilhado entre ambientes
        localStorage.removeItem(CURRENT_CACHE_KEY_PREFIX);
    }

    private isAuthFailure(err: unknown): boolean {
        const status =
            err instanceof HttpErrorResponse
                ? err.status
                : (err as { status?: number })?.status;
        return status === 401 || status === 403;
    }
}
